import { Info } from "lucide-react";
import { Button } from "components/ui/Button";
import {
  AutoResponseRule,
  DEFAULT_AUTO_RESPONSE_RULE,
} from "components/CondoManagerWhatsappCard";

type Tr = (es: string, en: string) => string;

const WEEKDAY_OPTIONS = [
  { value: 0, es: "Lun", en: "Mon" },
  { value: 1, es: "Mar", en: "Tue" },
  { value: 2, es: "Mié", en: "Wed" },
  { value: 3, es: "Jue", en: "Thu" },
  { value: 4, es: "Vie", en: "Fri" },
  { value: 5, es: "Sáb", en: "Sat" },
  { value: 6, es: "Dom", en: "Sun" },
];

export type DisturbScheduleCardProps = {
  tr: Tr;
  locale: "es" | "en";
  title: string;
  description: string;
  rules: AutoResponseRule[];
  onAddRule: () => void;
  onRemoveRule: (index: number) => void;
  onUpdateRule: (index: number, patch: Partial<AutoResponseRule>) => void;
  onToggleRuleWeekday: (index: number, weekday: number) => void;
  showHeader?: boolean;
};

export function DisturbScheduleCard({
  tr,
  locale,
  title,
  description,
  rules,
  onAddRule,
  onRemoveRule,
  onUpdateRule,
  onToggleRuleWeekday,
  showHeader = true,
}: DisturbScheduleCardProps) {
  return (
    <div
      className={
        showHeader
          ? "rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 sm:p-4 space-y-2.5"
          : "space-y-2.5"
      }
    >
      {showHeader ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="text-base font-semibold tracking-tight text-gray-900">
            {title}
          </h2>
          <span className="inline-flex items-center gap-1 text-xs leading-snug text-gray-600">
            <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
            <span>{description}</span>
          </span>
        </div>
      ) : (
        <p className="text-xs leading-snug text-gray-600">{description}</p>
      )}
      <div className="space-y-2.5">
        {rules.map((rule, index) => (
          <div
            key={`disturb-rule-${index}`}
            className="rounded-md border border-gray-300 p-3 space-y-2"
          >
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={rule.enabled !== false}
                  onChange={(e) =>
                    onUpdateRule(index, { enabled: e.target.checked })
                  }
                />
                {tr("Horario activo", "Availability rule enabled")}
              </label>
              <button
                type="button"
                className="text-xs text-red-600 hover:underline"
                onClick={() => onRemoveRule(index)}
              >
                {tr("Eliminar", "Remove")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_OPTIONS.map((option) => {
                const selected = (rule.weekdays || []).includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onToggleRuleWeekday(index, option.value)}
                    className={`px-2 py-1 rounded text-xs border ${
                      selected
                        ? "bg-blue-100 border-blue-300 text-blue-700"
                        : "bg-white border-gray-300 text-gray-600"
                    }`}
                  >
                    {locale === "es" ? option.es : option.en}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-600">{tr("Desde", "From")}</span>
                <input
                  type="time"
                  value={rule.start || "09:00"}
                  onChange={(e) => onUpdateRule(index, { start: e.target.value })}
                  className="rounded border border-gray-300 px-2 py-1 text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-600">{tr("Hasta", "To")}</span>
                <input
                  type="time"
                  value={rule.end || "18:00"}
                  onChange={(e) => onUpdateRule(index, { end: e.target.value })}
                  className="rounded border border-gray-300 px-2 py-1 text-xs"
                />
              </div>
            </div>
          </div>
        ))}
        <Button className="shrink-0" onClick={onAddRule} size="sm">
          {tr("Agregar horario disponible", "Add available hours")}
        </Button>
      </div>
    </div>
  );
}

export { DEFAULT_AUTO_RESPONSE_RULE };
